
import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/base/Button';

export const HeroSection: React.FC<{ setActiveCategory: (category: string) => void }> = ({ setActiveCategory }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const heroImages = [
    {
      url: "https://readdy.ai/api/search-image?query=Luxurious%20modern%20real%20estate%20portfolio%20showcasing%20elegant%20waterfront%20properties%2C%20contemporary%20apartments%2C%20and%20premium%20beach%20houses%20with%20sophisticated%20architecture%2C%20pristine%20landscaping%20and%20upscale%20interior%20design%2C%20professional%20real%20estate%20photography%20with%20warm%20golden%20lighting%20creating%20inviting%20atmosphere%2C%20high-end%20property%20development%20with%20clean%20minimalist%20backgrounds&width=1920&height=1080&seq=luca-homes-hero-1&orientation=landscape",
      title: "Find Your Perfect",
      subtitle: "Dream Property",
      section: "property-categories"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Ultra-luxury%20mega%20yacht%20sailing%20on%20crystal%20clear%20blue%20ocean%20waters%20with%20pristine%20white%20hull%20and%20modern%20design%2C%20premium%20yacht%20with%20multiple%20decks%20and%20sophisticated%20architecture%2C%20golden%20hour%20lighting%20creating%20elegant%20reflections%20on%20water%2C%20professional%20yacht%20photography%20showcasing%20maritime%20luxury%20and%20exclusivity%20with%20tropical%20coastline%20in%20background&width=1920&height=1080&seq=luca-homes-hero-2&orientation=landscape",
      title: "Discover Luxury",
      subtitle: "Yacht Collection",
      section: "property-categories"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Stunning%20modern%20beach%20house%20with%20contemporary%20architecture%20overlooking%20pristine%20coastline%2C%20luxury%20beachfront%20villa%20with%20expansive%20windows%20and%20outdoor%20terraces%2C%20tropical%20landscape%20with%20palm%20trees%20and%20white%20sand%20beach%2C%20contemporary%20coastal%20design%20with%20clean%20lines%2C%20professional%20real%20estate%20photography%20at%20sunset%20creating%20warm%20ambiance&width=1920&height=1080&seq=luca-homes-hero-3&orientation=landscape",
      title: "Exclusive Beach",
      subtitle: "House Retreats",
      section: "property-categories"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Modern%20luxury%20apartment%20building%20with%20glass%20facade%20and%20contemporary%20architecture%2C%20premium%20residential%20complex%20with%20sophisticated%20design%2C%20urban%20luxury%20living%20with%20city%20skyline%20views%2C%20professional%20real%20estate%20photography%20showcasing%20high-end%20apartment%20living%20with%20elegant%20exterior%20and%20landscaping&width=1920&height=1080&seq=luca-homes-hero-4&orientation=landscape",
      title: "Premium Urban",
      subtitle: "Apartments",
      section: "property-categories"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Elegant%20luxury%20furniture%20showroom%20with%20premium%20dining%20sets%2C%20sophisticated%20office%20furniture%2C%20and%20high-end%20home%20decor%2C%20professional%20furniture%20photography%20with%20warm%20lighting%20highlighting%20craftsmanship%20and%20premium%20materials%2C%20modern%20showroom%20setting%20with%20beautiful%20interior%20design&width=1920&height=1080&seq=luca-homes-hero-5&orientation=landscape",
      title: "Exquisite",
      subtitle: "Furniture Collection",
      section: "property-categories"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Luxury%20sports%20car%20on%20a%20scenic%20road%2C%20high-performance%20vehicle%20with%20sleek%20design%2C%20professional%20automotive%20photography%20showcasing%20speed%20and%20elegance&width=1920&height=1080&seq=stepitluxuryeverythingooooooo-hero-6&orientation=landscape",
      title: "High-Performance",
      subtitle: "Luxury Cars",
      section: "property-categories"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
        setIsAnimating(false);
      }, 500); // Duration of the fade-out animation
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCategoryClick = (categorySlug: string) => {
    scrollToSection('property-categories'); // Scroll to the PropertyCategories section
    setActiveCategory(categorySlug); // Set the active category in the parent component
  };

  const currentImage = heroImages[currentImageIndex];

  return (
    <section 
      id="hero"
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-in-out cursor-pointer"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${currentImage.url}')`
      }}
      onClick={() => scrollToSection(currentImage.section)}
    >
      {/* Animated overlay */}
      <div className={`absolute inset-0 bg-black transition-opacity duration-500 ${isAnimating ? 'opacity-20' : 'opacity-0'}`}></div>
      
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-24 sm:py-28 md:py-32 text-center text-white relative z-10">
          <h1 className={`text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight transition-all duration-700 ${isAnimating ? 'transform translate-y-4 opacity-0' : 'transform translate-y-0 opacity-100'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
            {currentImage.title}
            <span className="block text-blue-400">{currentImage.subtitle}</span>
          </h1>
          
          <p className={`text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 md:mb-12 text-gray-200 max-w-4xl mx-auto leading-relaxed px-2 sm:px-4 transition-all duration-700 delay-200 ${isAnimating ? 'transform translate-y-4 opacity-0' : 'transform translate-y-0 opacity-100'}`}>
            Discover your next home, investment, or luxury item with our curated selection of properties and exclusive listings.
          </p>
          
          
          <div className={`flex flex-col xs:flex-row gap-2 xs:gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12 md:mb-16 px-2 sm:px-4 transition-all duration-700 delay-400 ${isAnimating ? 'transform translate-y-4 opacity-0' : 'transform translate-y-0 opacity-100'}`}>
            <Button 
              variant="primary" 
              size="lg" 
              className="w-full xs:w-auto px-6 xs:px-8 sm:px-12 py-2.5 xs:py-3 sm:py-4 text-sm xs:text-base sm:text-lg whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryClick('all'); // Navigate to properties page with 'all' category
              }}
            >
              <i className="ri-search-line mr-1.5 xs:mr-2 sm:mr-3"></i>
              Explore Properties
            </Button>
            <Button 
              variant="secondary" 
              size="lg" 
              className="w-full xs:w-auto px-6 xs:px-8 sm:px-12 py-2.5 xs:py-3 sm:py-4 text-sm xs:text-base sm:text-lg bg-white/10 border-white text-white hover:bg-white hover:text-gray-900 whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                // handleAIAssistant(); // Assuming this function exists elsewhere or will be defined
              }}
            >
              <i className="ri-robot-2-line mr-1.5 xs:mr-2 sm:mr-3"></i>
              AI Assistant
            </Button>
          </div>

          {/* Property Categories Grid - Ultra Responsive */}
          <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 xs:gap-3 sm:gap-4 md:gap-6 lg:gap-8 text-center px-2 sm:px-4 transition-all duration-700 delay-500 ${isAnimating ? 'transform translate-y-4 opacity-0' : 'transform translate-y-0 opacity-100'}`}>
            <div 
              className="bg-white/10 backdrop-blur-sm rounded-lg xs:rounded-xl sm:rounded-2xl p-2 xs:p-3 sm:p-4 md:p-6 hover:bg-white/20 transition-all cursor-pointer transform hover:scale-105 duration-300"
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryClick('boats');
              }}
            >
              <i className="ri-ship-line text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-1 xs:mb-2 sm:mb-3 text-blue-400"></i>
              <h3 className="font-semibold text-xs xs:text-sm sm:text-base md:text-lg">Boats</h3>
              <p className="text-gray-300 text-xs hidden sm:block">Luxury vessels</p>
            </div>
            <div 
              className="bg-white/10 backdrop-blur-sm rounded-lg xs:rounded-xl sm:rounded-2xl p-2 xs:p-3 sm:p-4 md:p-6 hover:bg-white/20 transition-all cursor-pointer transform hover:scale-105 duration-300"
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryClick('apartments');
              }}
            >
              <i className="ri-building-line text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-1 xs:mb-2 sm:mb-3 text-blue-400"></i>
              <h3 className="font-semibold text-xs xs:text-sm sm:text-base md:text-lg">Apartments</h3>
              <p className="text-gray-300 text-xs hidden sm:block">Modern living</p>
            </div>
            <div 
              className="bg-white/10 backdrop-blur-sm rounded-lg xs:rounded-xl sm:rounded-2xl p-2 xs:p-3 sm:p-4 md:p-6 hover:bg-white/20 transition-all cursor-pointer transform hover:scale-105 duration-300"
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryClick('buildings');
              }}
            >
              <i className="ri-building-2-line text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-1 xs:mb-2 sm:mb-3 text-blue-400"></i>
              <h3 className="font-semibold text-xs xs:text-sm sm:text-base md:text-lg">Buildings</h3>
              <p className="text-gray-300 text-xs hidden sm:block">Commercial spaces</p>
            </div>
            <div 
              className="bg-white/10 backdrop-blur-sm rounded-lg xs:rounded-xl sm:rounded-2xl p-2 xs:p-3 sm:p-4 md:p-6 hover:bg-white/20 transition-all cursor-pointer transform hover:scale-105 duration-300"
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryClick('beach-houses');
              }}
            >
              <i className="ri-home-smile-line text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-1 xs:mb-2 sm:mb-3 text-blue-400"></i>
              <h3 className="font-semibold text-xs xs:text-sm sm:text-base md:text-lg">Beach Houses</h3>
              <p className="text-gray-300 text-xs hidden sm:block">Coastal retreats</p>
            </div>
            <div 
              className="bg-white/10 backdrop-blur-sm rounded-lg xs:rounded-xl sm:rounded-2xl p-2 xs:p-3 sm:p-4 md:p-6 hover:bg-white/20 transition-all cursor-pointer transform hover:scale-105 duration-300 col-span-2 sm:col-span-1"
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryClick('furniture');
              }}
            >
              <i className="ri-sofa-line text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-1 xs:mb-2 sm:mb-3 text-blue-400"></i>
              <h3 className="font-semibold text-xs xs:text-sm sm:text-base md:text-lg">Furniture</h3>
              <p className="text-gray-300 text-xs hidden sm:block">Premium pieces</p>
            </div>
            <div 
              className="bg-white/10 backdrop-blur-sm rounded-lg xs:rounded-xl sm:rounded-2xl p-2 xs:p-3 sm:p-4 md:p-6 hover:bg-white/20 transition-all cursor-pointer transform hover:scale-105 duration-300 col-span-2 sm:col-span-1"
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryClick('cars');
              }}
            >
              <i className="ri-car-line text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-1 xs:mb-2 sm:mb-3 text-blue-400"></i>
              <h3 className="font-semibold text-xs xs:text-sm sm:text-base md:text-lg">Cars</h3>
              <p className="text-gray-300 text-xs hidden sm:block">Luxury vehicles</p>
            </div>
          </div>

          {/* AI Assistant Promotion */}
          <div className={`mt-8 sm:mt-12 md:mt-16 bg-gradient-to-r from-blue-600/20 to-teal-600/20 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-white/20 transition-all duration-700 delay-600 ${isAnimating ? 'transform translate-y-4 opacity-0' : 'transform translate-y-0 opacity-100'}`}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
              <div className="text-center sm:text-left">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2">Need Help Choosing?</h3>
                <p className="text-sm sm:text-base text-gray-200">Our AI assistant can help you find the perfect property and schedule viewings</p>
              </div>
              <Button 
                variant="primary" 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold whitespace-nowrap"
                onClick={(e) => {
                  e.stopPropagation();
                  // handleAIAssistant(); // Assuming this function exists elsewhere or will be defined
                }}
              >
                <i className="ri-robot-2-line mr-2"></i>
                Try AI Assistant
              </Button>
            </div>
          </div>

          {/* Image indicators */}
          <div className="flex justify-center space-x-1.5 xs:space-x-2 mt-6 sm:mt-8">
            {heroImages.map((_, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                }}
                className={`w-1.5 h-1.5 xs:w-2 xs:h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentImageIndex ? 'bg-blue-400 scale-125' : 'bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        </div>
    </section>
  );
};
